import { ReactNode } from "react";
import { Box, LinearProgress } from "@material-ui/core";
import useFirestore from "hooks/useFirestore";
import useWebotAdmin from "hooks/useWebotAdmin";
import PaidCustomerGate from "components/PaidCustomerGate";

// ----------------------------------------------------------------------

type PaidCustomerGuardProps = {
  children: ReactNode;
};

/**
 * Restricts the dashboard to paying customers — users with at least one
 * `advancedFeatures` entry. Webot staff (identified via the admins doc, a
 * concept orthogonal to `advancedFeatures`) always pass through so they can
 * reach their admin pages.
 *
 * Sits after NewUserGuard in the route tree, so it only ever renders for an
 * authenticated user who has finished onboarding.
 */
export default function PaidCustomerGuard({ children }: PaidCustomerGuardProps) {
  const { userSettings, isLoadingUser } = useFirestore();
  const { isWebotAdmin, isLoadingAdminStatus } = useWebotAdmin();

  // Wait for both async loads before deciding — `isLoadingUser` can be false
  // while `userSettings` is still null on first render, so also guard on that
  // (same idiom as FeatureGate) to avoid flashing the gate.
  if (isLoadingUser || !userSettings || isLoadingAdminStatus) {
    return (
      <Box my={4} p={2} sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  const hasAdvancedFeature = (userSettings.advancedFeatures?.length ?? 0) > 0;

  if (!hasAdvancedFeature && !isWebotAdmin) {
    return <PaidCustomerGate />;
  }

  return <>{children}</>;
}
